import { Button, Col, Divider, Form, Modal, Row, Table, TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// components
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';
import StatusComponent from 'components/status/status';
import TableTotal from 'components/tableTotal/tableTotal';
import CommitteeMembers from './committeeMembers';
import CommitteeSettings from './committeeSettings';

// services
import { isRTL } from 'core/services/language';
import { onGetCommitteeMeetingsList } from '../services/meetingServices';

// models
import { Committee } from '../models/committee';
import { CommitteeMember } from '../models/members';
import { CommitteeMeeting } from '../models/meetings';

// utils
import { StatusColors } from 'utils/statusColors';
import { dateFormat } from 'utils/dateFormat';

// styles
import styles from '../committees.module.scss';
import { onGetCommitteeMembers } from '../services/memberServices';
import { onGetActiveCommittees, onGetCommitteesList } from '../services/committeeServices';
import SelectElement from 'components/selectElement/selectElement';

interface ActiveCommitteeModalProps {
	open: boolean;
	type: 'documents' | 'meetings';
	onClose: () => void;
	onSelect?(committeeId: string): void;
}

const ActiveCommitteeModal: FunctionComponent<ActiveCommitteeModalProps> = ({ open, type, onSelect, onClose }) => {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState<boolean>(true);

	const [activeCommitteesOptions, setActiveCommitteesOptions] = useState<{ label: string; value: string }[]>([]);
	const [selectedCommittee, setSelectedCommittee] = useState<string>();

	useEffect(() => {
		if (open) getCommittees();
	}, [open]);

	const getCommittees = async () => {
		try {
			setIsLoading(true);

			const response = await onGetActiveCommittees();

			setActiveCommitteesOptions(
				response.map((committee: Committee) => ({
					label: isRTL() ? committee.name.ar : committee.name.en,
					value: committee.id.toString(),
				})),
			);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
		}
	};

	return (
		<Modal
			open={open}
			title={type === 'meetings' ? t('ACTIONS.SELECT_COMMITTEE') : t('ACTIONS.SELECT_COMMITTEE_DOCUMENT')}
			onCancel={onClose}
			onClose={onClose}
			footer={[
				<Button key="discard" type="link" danger size="middle" onClick={onClose}>
					{t('COMMON.CANCEL')}
				</Button>,
				<Button
					key="confirm"
					variant="solid"
					color="primary"
					size="middle"
					disabled={!selectedCommittee}
					onClick={() => onSelect && selectedCommittee && onSelect(selectedCommittee)}>
					{t('COMMON.CONFIRM')}
				</Button>,
			]}>
			<div className="py-3">
				<SelectElement
					label=""
					options={activeCommitteesOptions}
					fullWidth
					isLoading={isLoading}
					onChange={(value) => setSelectedCommittee(value.toString())}
				/>
			</div>
		</Modal>
	);
};

export default ActiveCommitteeModal;
